import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CacheBuster from 'react-cache-buster';
import { isValidJSObject, removeURLParam } from "client/classes/utilities/Misc";
import AIAPIUtility from "client/classes/ai/AIAPI";
import { CustomEventTypes, FocusChange, LayoutOrientations } from "client/globals";
import PlaceholderContent from "client/components/layout/PlaceholderContent";
import Panel from "client/components/layout/Panel";
import Spacer from "client/components/layout/Spacer";
import Button from "client/components/layout/Button";
import CrashReportView from "client/components/CrashReportView";
import { disableBrowserNativeInput } from "client/classes/utilities/WorkspaceUtils";
import Utils from "client/classes/Utilities";
import EulaDisplay from "client/components/welcome/EulaDisplay";
import ModalRenderer from "client/components/layout/ModalRenderer";
import { ApolloGQLClient } from "client/classes/api/GQL/ApolloGQLClient";
import AssetUtility from "client/classes/AssetUtility";
import Input from "client/classes/Input";
import useEventListener from "client/components/reactUtils/useEventListener";
import { AppState } from "client/classes/AppState";
import DropDownLayer from "client/components/layers/DropdownLayer";
import TooltipDisplay from "client/components/tooltips/TooltipDisplay";
import Scenario from "client/classes/scenarioElements/Scenario";
import Engine from "client/classes/Engine";
import GlobalEventHandler from "client/components/GlobalEventHandler";
import SessionUtils from "client/classes/utilities/SessionUtils";
import PreferencesUtils from "client/classes/utilities/PreferencesUtils";
import config from './config';
import { useTypedDispatch, useTypedSelector } from "./state/hooks";
import { StoreActions } from "./state/store";
import Dashboard from "./components/dashboard/Dashboard";
import { ReactComponent as WorkLinkLogo } from './_icons/worklink.svg';
import Statusbar from "./components/statusbar/Statusbar";
import Viewport from "./components/viewport/Viewport";
import { GQLSession } from "./classes/api/GQL/scenarios/GQLSession";

const App = () => 
{
	const dispatch = useTypedDispatch();

	const sessionInfo = useTypedSelector(state => state.userModule.sessionInfo);
	const crashError = useTypedSelector((state) => state.applicationModule.crashError);
	const loadedScenarioId = useTypedSelector((state) => state.scenarioModule.scenarioId);
	const currentStepId = useTypedSelector((state) => state.scenarioModule.currentStepId);
	
	const [sessionError, setSessionError] = useState(0);

	const isProduction = window._env_.ENVIRONMENT === "production";
	const confirmedEula = sessionInfo?.confirmed_eula || "0";
	const isEulaRequired = Utils.compareVersionNumbers(confirmedEula, config.EULA_VERSION) < 0;

	useEventListener(CustomEventTypes.onProjectLoadStart, () => 
	{
		// we need to replace the projectid param with the scenario and/or save id
		removeURLParam("projectid");
	}, document);

	useEventListener(CustomEventTypes.onProjectLoadEnd, () => 
	{
		dispatch(StoreActions.workspace.setSpritesVisible(false));
		Engine.getInstance().setSpritesVisibility(false);
		dispatch(StoreActions.application.setSimulationAsViewer(true));
		AppState.startSimulation(true);
	}, document);

	useEffect(() => 
	{
		//obtain token and save to local storage + default axios header
		const params = new URLSearchParams(window.location.search);
		let token: string | null | undefined = params.get('user_token');
		if (token)
		{
			localStorage.setItem("accessToken", token);
			window.location.href = window.location.pathname; //refresh for removing the token from the browser's url field

		}
		else // we need to save and refresh the page
		{
			token = localStorage.getItem("accessToken") ?? config.ACCESS_TOKEN;
		}

		const refreshSessionInfo = async () =>
		{
			if (!token)
			{
				window.location.href = config.CMS_LOGIN_ADDRESS + '?callback=' + window.location + '&product=worklink';
			}

			// set the token for GQL client
			ApolloGQLClient.jwt = token as string;

			const response = await GQLSession.getSessionInfo()

			const { sessionInfo: data, errors } = response;
			if (data)
			{
				localStorage.setItem("accessToken", data.accessToken);
				dispatch(StoreActions.user.setUserSessionData(data));
				ApolloGQLClient.permalink = data?.companies[0]?.permalink ?? "";
				AssetUtility.setJwtToken(data.accessToken);
				const userLicense = SessionUtils.getDominantLicense(data.licenses || []);
				dispatch(StoreActions.application.setIsViewOnlyMode(userLicense === "Viewer"));
			}
			
			if(errors && errors.length > 0)
			{
				for(const error of errors)
				{
					//unauthorized
					if (error.code === 401)
					{
						window.location.href = config.CMS_LOGIN_ADDRESS + '?callback=' + window.location + '&product=worklink';
					}

					// no c2 access
					if (error.code === 403)
					{
						setSessionError(403);
					}
					console.error(error.message);
				}
			}

			await new Promise(resolve => setTimeout(resolve, Number(config.SESSION_REFRESH_TIME_IN_SECONDS || 86400) * 1000)); // 86400 is the default value defined in config.ts
			refreshSessionInfo();
		};
		refreshSessionInfo();

		disableBrowserNativeInput();

		// subscribe to certain window events
		window.onblur = () =>
		{
			Input.clearAll();
			document.dispatchEvent(new CustomEvent(CustomEventTypes.applicationFocusChanged, { detail: { change: FocusChange.FocusLost } }));
			dispatch(StoreActions.application.setApplicationFocus(false));
		};

		window.onfocus = () =>
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.applicationFocusChanged, { detail: { change: FocusChange.FocusGained } }));
			dispatch(StoreActions.application.setApplicationFocus(true));
		};
	}, []);

	useEffect(() => 
	{
		if(sessionInfo && isValidJSObject(sessionInfo))
		{
			axios.defaults.headers.common['Authorization'] = `Bearer ${sessionInfo.accessToken}`;

			AIAPIUtility.initialize();
			PreferencesUtils.loadWlcSettings(sessionInfo);
			Input.disableKeybinds();
			loadWorklinkSettings();
		}
	}, [sessionInfo]);	

	useEffect(() => 
	{
		let step = Scenario.getStep(currentStepId);
		// skip CPS steps no matter when we encounter them
		if(step?.isTrackersStep())
		{
			step = step.getNextStep();
			if(step)
			{
				Scenario.setCurrentStep(step);
			}
		}

		if(step)
		{
			Engine.getInstance().setCurrentStep(step);
		}
	}, [currentStepId]);

	const errorMessage = useMemo(() => 
	{
		switch (sessionError)
		{
			case 403:
				return (
					<>
						You do not have access to the WorkLink Authoring Platform.
						<br />
						Contact your administrator to make sure you've got an authoring license associated with your account.
						<br />
						For further inquiries, please <a className="hugeText highlighted" href="https://www.scopear.com/contact/" target="_blank" rel="noopener noreferrer help">contact us</a>
					</>
				);
			default:
				return (
					<>
						Error {sessionError}
						<br />
						An error has occured while starting session.
						<br />
						Please <a className="hugeText highlighted" href="https://scopear.zendesk.com/hc/en-us/requests/new" target="_blank" rel="noopener noreferrer help">contact support</a> to report the problem.
					</>
				);
		}
	}, [sessionError]);

	const loadWorklinkSettings = useCallback(() => 
	{
		if(sessionInfo)
		{
			const settings = JSON.parse(sessionInfo.wlc_settings).worklink;
			if(settings)
			{
				dispatch(StoreActions.worklink.setSettings(settings));
			}
		}
	}, [sessionInfo]);

	if (sessionError)
	{
		return (
			<div className={"screenCover dark"}>
				<PlaceholderContent>
					<Panel orientation={LayoutOrientations.Horizontal}
						style={{
							overflow: "hidden",
						}}
					>
						<Spacer stretchTo={{ x: "100%" }} />
						<img className="scopeLogo" src="../../../scopelogo.png" width="88" height="88" alt="scopeAR logo" />
						<Spacer stretchTo={{ x: "100%" }} />
					</Panel>
					<Spacer size={{ y: "16px" }} />
					<div className={"hugeText"}
						style={{
							textAlign: "center",
						}}
					>
						{ errorMessage }
					</div>
					<Spacer size={{ y: "16px" }} />
					<Button
						data-name={"login-reload"}
						category={"Session"}
						label={"Try with a different email"}
						onClick={() =>
						{
							localStorage.removeItem("accessToken");
							window.location.reload();
						}}
					/>
				</PlaceholderContent>
			</div>
		);
	}

	if (crashError)
	{
		return (
			<CrashReportView />
		);
	}

	if(isValidJSObject(sessionInfo) && isEulaRequired)
	{
		return (
			<div style={{
				overflow: "scroll",
				height: "100%"
			}}>
				<EulaDisplay />
			</div>
		);
	}

	return (
		<CacheBuster
			currentVersion={config.webViewVersion}
			isEnabled={isProduction}
		>
			{
				isValidJSObject(sessionInfo) ?
					<div id="App">
						{
							!loadedScenarioId ?
								<Dashboard />
								:
								<>
									<div data-name={"viewport-wrapper"}
										className={"viewportWrapper"}
									>
										<Viewport />
									</div>
									<Statusbar />
								</>
						}
						<ModalRenderer />
						<TooltipDisplay forceTooltip={true} />
						<DropDownLayer />
						<GlobalEventHandler />
					</div>
					:
					<div className={"fullScreen dark loadingScreen"}
						data-name={"loading-screen"}
					>
						<WorkLinkLogo className={"centered"} />
						<div className={"smallText"}>
							Checking connection...
						</div>
					</div>
			}
		</CacheBuster>
	);
};

export default App;
